import { cardBookProposalIds } from './bookActionsPanel.js'
import type { DeckCard, DeckResponse } from './types'

/** Server-owned manual source provenance, never inferred from a missing document. */
export interface ManualBookRouting {
  source: 'manual_entry'
  group_id: string
  fund_id: string
  creator_id: string
}

export interface ManualBookSelection {
  context: ManualBookRouting
  proposalIds: readonly string[]
}

export function sameManualBookContext(
  left: ManualBookRouting | null | undefined,
  right: ManualBookRouting | null | undefined
): boolean {
  return (
    !!left &&
    !!right &&
    left.source === 'manual_entry' &&
    right.source === 'manual_entry' &&
    !!left.group_id &&
    !!left.fund_id &&
    !!left.creator_id &&
    left.group_id === right.group_id &&
    left.fund_id === right.fund_id &&
    left.creator_id === right.creator_id
  )
}

export function manualCardProposalIds(card: DeckCard): string[] {
  return [
    ...new Set(
      [
        ...cardBookProposalIds(card),
        card.question?.proposal_id,
        card.issuer_panel?.proposal_id,
      ].filter((id): id is string => !!id)
    ),
  ]
}

/** Keep the server card intact. A card spanning an unselected sibling cannot be acted on. */
export function selectedManualCards(
  deck: DeckResponse,
  selection: ManualBookSelection
): readonly DeckCard[] | null {
  const selected = new Set(selection.proposalIds)
  if (
    !selected.size ||
    selected.size !== selection.proposalIds.length ||
    deck.group_id !== selection.context.group_id ||
    deck.document_id ||
    !deck.deck_version ||
    !deck.fund_ids?.includes(selection.context.fund_id) ||
    !sameManualBookContext(deck.manual_book_context, selection.context)
  )
    return null
  const cards = deck.cards.filter((card) =>
    manualCardProposalIds(card).some((id) => selected.has(id))
  )
  const seen = new Set<string>()
  for (const card of cards) {
    if (card.stage !== 'book' && card.stage !== 'decide') return null
    for (const id of manualCardProposalIds(card)) {
      if (!selected.has(id)) return null
      seen.add(id)
    }
  }
  // Only actual same-group terminal statuses from this fresh GET explain disappearance.
  // A local onDone callback may represent partial approval or leave-pending.
  if (
    selection.proposalIds.some(
      (id) =>
        !seen.has(id) &&
        !['confirmed', 'rejected'].includes(deck.manual_book_proposal_statuses?.[id] ?? '')
    )
  )
    return null
  return cards
}
